import {
  type ChangeEvent,
  type FC,
  type KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { KeyCodes } from '@/consts/keys.ts'
import { LocalStorageKeys } from '@/consts/localStorage'
import type { DialogProps } from '@/dialogs/types'
import { MESSAGE_PRICE_RANGE } from '@/modules/chatting/ui/chat-window/constants'
import { validateMessagePrice } from '@/utils/message-price.ts'

interface Props extends DialogProps {
  userId: number
  onSave: (price: number) => void
  defaultPrice?: number
  messageMinPrice?: number
  messageMaxPrice?: number
}

export const ChatPriceDialog: FC<Props> = ({
  userId,
  isOpen,
  onClose,
  onSave,
  defaultPrice,
  messageMinPrice = MESSAGE_PRICE_RANGE.MIN,
  messageMaxPrice = MESSAGE_PRICE_RANGE.MAX,
}) => {
  const [price, setPrice] = useState(
    defaultPrice ? defaultPrice.toString() : '',
  )
  const inputRef = useRef<HTMLInputElement>(null)

  const handlePriceChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value
      const newValue = !value
        ? ''
        : value.startsWith('.')
          ? `0${value}`
          : value.indexOf('.') >= 0
            ? value.slice(0, value.indexOf('.') + 3)
            : value

      setPrice(newValue)
    },
    [],
  )

  const isPriceValid = validateMessagePrice(
    price,
    messageMinPrice,
    messageMaxPrice,
  )

  const handleSave = useCallback(() => {
    // TODO: Remove direct storage call after dialogs refactoring
    const sessionData = sessionStorage.getItem(LocalStorageKeys.FAN_MESSAGE)
    const fanMessageDataObjectCopy = sessionData ? JSON.parse(sessionData) : {}

    fanMessageDataObjectCopy[`${userId}`] = {
      ...fanMessageDataObjectCopy[`${userId}`],
      price,
    }
    sessionStorage.setItem(
      LocalStorageKeys.FAN_MESSAGE,
      JSON.stringify(fanMessageDataObjectCopy),
    )
    onSave(Number(price))
    onClose()
  }, [onClose, onSave, price, userId])

  const handleKeyEvent = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.code === KeyCodes.ENTER && isPriceValid) {
        event.stopPropagation()
        event.preventDefault()
        handleSave()
      }
    },
    [handleSave, isPriceValid],
  )

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      initialFocus={inputRef}
    >
      <Dialog.Header>
        <Dialog.Title>Message Price</Dialog.Title>
      </Dialog.Header>

      <div className="mt-6">
        <label className="text-sm font-medium text-text-default">Price</label>
        <TextField
          ref={inputRef}
          value={price}
          onChange={handlePriceChange}
          type="number"
          prefix={<span className="text-secondary-300">$</span>}
          placeholder="Free"
          inputClassName="placeholder:text-text-default"
          className="h-[40px]"
          pattern="[0-9+-.]+"
          autoFocus
          onKeyDown={handleKeyEvent}
        />
        <p className="mt-1 text-xs text-secondary-300">
          Minimum ${messageMinPrice} or Free
        </p>
        {!isPriceValid && (
          <p className="mt-1 text-xs text-danger-default">
            Please enter a valid price between ${messageMinPrice} and $
            {messageMaxPrice}
          </p>
        )}
      </div>

      <Dialog.Footer className="flex gap-2">
        <Button
          onClick={onClose}
          className="flex-1"
          variant="outline"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="flex-1"
          disabled={!isPriceValid}
        >
          Save
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
